import { Node, mergeAttributes } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { FormFieldDto } from "../../general/dtos";
import { markdownHtml, markdownText } from '../../general/markdown';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export interface QuizTargetLabelRequest {
    label: string;
    onSave: (label: string) => void;
}

export const FormFieldNode = Node.create({
    name: "formField",
    group: "inline",
    inline: true,
    atom: true,

    addOptions() {
        return {
            iframeDocument: Document,
            onGetField: (fieldKey: string): FormFieldDto | null => null,
            onOpenFieldEditor: (field: FormFieldDto): void => {},
            onEditQuizLabel: null as ((request: QuizTargetLabelRequest) => void) | null,
        };
    },

    addAttributes() {
        return {
            // Populated only on the copied slice; pasted nodes clear it before insertion.
            clipboardField: {
                default: null,
                parseHTML: element => element.getAttribute('data-form-field-definition'),
                renderHTML: attributes => attributes.clipboardField ? { 'data-form-field-definition': attributes.clipboardField } : {},
            },
            key: { default: null, },
            "field-key": { default: null, },
            "expected-key": { default: null, },
            "results-label": { default: null, },
        };
    },

    parseHTML() {
        return [
        {
            tag: "form-field",
            getAttrs: element => ({
                key: element.getAttribute("key"),
            }),
        },
        {
            tag: "quiz-target",
            getAttrs: (element) => ({
                "field-key": (element as HTMLElement).getAttribute("field-key"),
                "expected-key": (element as HTMLElement).getAttribute("expected-key"),
            }),
        },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const isQuiz = !!HTMLAttributes["expected-key"];
        const tagName = isQuiz ? "quiz-target" : "form-field";

        return [tagName, mergeAttributes(HTMLAttributes)];
    },

    addProseMirrorPlugins() {
        return [new Plugin({
            props: {
                decorations: state => {
                    const { from, to, empty } = state.selection;
                    if (empty) return DecorationSet.empty;
                    const decorations: Decoration[] = [];
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type.name === this.name && pos >= from && pos + node.nodeSize <= to) {
                            decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'field-selection-highlight' }));
                        }
                    });
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        })];
    },

    addNodeView() {
        // All node views in this editor share one numbering pass per document.
        let numberedDocument: ProseMirrorNode | null = null;
        let numberedFields = new Map<string,number>();
        let fieldsRefresh: Transaction | null = null;
        return (props) => {
            const doc = this.options.iframeDocument || document;

            // const dom = document.createElement("span");
            const dom = doc.createElement("span");
            dom.className = "form-field-placeholder";

            const { onGetField, onOpenFieldEditor } = this.options;
        
            let currentNode = props.node;
            let renderedNode: ProseMirrorNode | null = null;
            let renderedField: FormFieldDto | null | undefined;
            let renderedLabel: string | undefined;

            const render = (force = false) => {
                const isQuiz = !!currentNode.attrs['expected-key'];
                const fieldKey = isQuiz ? currentNode.attrs['field-key'] : currentNode.attrs.key;
                const field: FormFieldDto | null = onGetField(fieldKey);
                if (!field) {
                    if (!force && renderedNode === currentNode && renderedField === field) return;
                    renderedNode = currentNode; renderedField = field;
                    dom.replaceChildren(); dom.onclick = null;
                    const icon = doc.createElement("i");
                    icon.className = "pi pi-exclamation-triangle mr-2";
                    dom.append(icon, doc.createTextNode(`Missing: ${fieldKey}`));
                    return;
                }

                let displayLabel = field.label;
                if (field.type === 'inline-text' && !field.label.trim()) {
                    if (numberedDocument !== props.editor.state.doc) {
                        numberedDocument = props.editor.state.doc; numberedFields = new Map();
                        let index = 0;
                        numberedDocument.descendants(node => {
                            if (node.type.name !== 'formField') return;
                            const candidate = onGetField(node.attrs.key);
                            if (candidate?.type === 'inline-text') {
                                if (!numberedFields.has(candidate.key)) numberedFields.set(candidate.key,index);
                                index++;
                            }
                        });
                    }
                    const index = numberedFields.get(field.key);
                    displayLabel = index !== undefined ? `Blank ${index + 1}` : 'Blank';
                }
                if (!force && renderedNode === currentNode && renderedField === field && renderedLabel === displayLabel) return;
                renderedNode = currentNode; renderedField = field; renderedLabel = displayLabel;
                dom.replaceChildren(); dom.onclick = null;
                const icon = doc.createElement("i");
                let tooltipMessage = `Type: ${field.type}\nLabel: ${markdownText(displayLabel)}`;
                if (isQuiz) {
                    const option = field.options?.find(o => o.key === currentNode.attrs['expected-key']);
                    const val = option?.value ?? "Unknown option";
                    displayLabel = val.length > 25 ? val.substring(0, 25) + "..." : val;
                    tooltipMessage = `Quiz Pool: ${markdownText(field.label)}\nCorrect Answer: ${val}`;
                }

                icon.className = `pi ${isQuiz ? 'pi-check-circle' : 'pi-pencil'} mr-2`;
                const label = doc.createElement("span");
                label.className = "node-text";
                label.title = tooltipMessage;
                if (isQuiz) label.textContent = displayLabel;
                else label.innerHTML = markdownHtml(displayLabel, true);
                dom.append(icon, label);
                dom.onclick = () => onOpenFieldEditor(field);
                if (isQuiz && this.options.onEditQuizLabel) {
                    if (currentNode.attrs['results-label']) label.title += `\nLabel for results: ${currentNode.attrs['results-label']}`;
                    const button = doc.createElement('button');
                    button.type = 'button';
                    button.className = 'quiz-results-label-button';
                    button.setAttribute('aria-label', `Label for results: ${field.options?.find(option => option.key === currentNode.attrs['expected-key'])?.value ?? 'quiz item'}`);
                    button.title = 'Edit this quiz item’s label for results';
                    button.style.cssText = 'display:inline-flex;align-items:center;margin-left:.3em;padding:.1em .2em;border:0;background:transparent;color:inherit;cursor:pointer;font:inherit;';
                    const tag = doc.createElement('i');
                    tag.className = 'pi pi-tag';
                    tag.setAttribute('aria-hidden', 'true');
                    button.appendChild(tag);
                    button.onmousedown = (event: MouseEvent) => event.preventDefault();
                    button.onclick = (event: MouseEvent) => {
                        event.stopPropagation();
                        const expectedKey = currentNode.attrs['expected-key'];
                        this.options.onEditQuizLabel?.({ label: currentNode.attrs['results-label'] ?? '', onSave: (value: string) => {
                            if (props.editor.isDestroyed) return;
                            const pos = props.getPos();
                            if (typeof pos !== 'number') return;
                            const target = props.editor.state.doc.nodeAt(pos);
                            if (target?.type.name !== this.name || target.attrs['field-key'] !== field.key || target.attrs['expected-key'] !== expectedKey) return;
                            props.editor.view.dispatch(props.editor.state.tr.setNodeMarkup(pos, undefined,
                                { ...target.attrs, 'results-label': value.trim() || null }));
                        } });
                    };
                    dom.appendChild(button);
                }
            };

            render();
            const refreshFields = ({ transaction }: { transaction: Transaction }) => {
                if (transaction.getMeta("formFieldsUpdated")) {
                    if (fieldsRefresh !== transaction) { numberedDocument = null; fieldsRefresh = transaction; }
                    render(true);
                } else if (transaction.docChanged && onGetField(currentNode.attrs.key)?.type === 'inline-text') render();
            };
            props.editor.on("transaction", refreshFields);

            return {
                dom,
                update: (node) => {
                    if (node.type !== currentNode.type) return false;
                    currentNode = node;
                    render();
                    return true;
                },
                destroy: () => {
                    props.editor.off("transaction", refreshFields);
                    dom.onclick = null;
                },
                stopEvent: event => dom.querySelector('button')?.contains(event.target as globalThis.Node) ?? false,
            };
        };
    }

});
